import { useState } from 'react'
import { Pencil, Eye } from 'lucide-react'
import { exportWorkflowJson } from '../io'
import { useOpsCenterStore } from '../store'
import type { Workflow } from '../types'

export interface JsonPanelProps {
  wf: Workflow
}

export function JsonPanel({ wf }: JsonPanelProps) {
  const updateWorkflow = useOpsCenterStore((s) => s.updateWorkflow)

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)

  const json = JSON.stringify(exportWorkflowJson(wf), null, 2)

  function startEditing() {
    setDraft(json)
    setError(null)
    setEditing(true)
  }

  function handleApply() {
    try {
      const parsed: unknown = JSON.parse(draft)
      const o = parsed as Record<string, unknown>
      updateWorkflow(wf.id, {
        tasks: (o.tasks ?? []) as Workflow['tasks'],
        name: typeof o.name === 'string' ? o.name : wf.name,
        version: typeof o.version === 'number' ? o.version : wf.version,
        tags: Array.isArray(o.tags) ? (o.tags as string[]) : wf.tags,
        variables: Array.isArray(o.variables) ? (o.variables as Workflow['variables']) : wf.variables,
      })
      setError(null)
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? `Invalid JSON: ${err.message}` : 'Invalid JSON')
    }
  }

  return (
    <div className="flex flex-col h-full rounded-lg border border-[#1e2c44] bg-[#0f1729] overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#1e2c44] shrink-0">
        <h3 className="text-xs font-semibold text-[var(--text-primary)]">Workflow JSON</h3>
        {editing ? (
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleApply}
              className="px-2 py-1 rounded text-[10px] font-medium bg-[#6366f1] text-white hover:bg-[#818cf8] transition-colors"
            >
              Apply
            </button>
            <button
              onClick={() => {
                setEditing(false)
                setError(null)
              }}
              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-[#5b6b85] hover:text-[var(--text-primary)] hover:bg-[#1a2740]"
            >
              <Eye size={11} /> View
            </button>
          </div>
        ) : (
          <button
            onClick={startEditing}
            className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-[#5b6b85] hover:text-[var(--text-primary)] hover:bg-[#1a2740]"
          >
            <Pencil size={11} /> Edit raw
          </button>
        )}
      </div>

      {error && (
        <div className="mx-3 mt-2 text-[10px] text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-2 py-1.5 shrink-0">
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto p-3">
        {editing ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            className="w-full h-full min-h-[300px] bg-[#0a1322] border border-[#1e2c44] rounded-md p-2 text-[10px] font-mono text-[var(--text-primary)] focus:outline-none focus:border-[#6366f1]/60 resize-none"
          />
        ) : (
          <pre className="text-[10px] font-mono text-[#94a3b8] whitespace-pre-wrap break-words">{json}</pre>
        )}
      </div>
    </div>
  )
}
